import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import Logo from "../components/Logo";
import { CheckCircle, XCircle, ArrowRight } from "@phosphor-icons/react";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const token = params.get("token") || "";
  const [state, setState] = useState("loading"); // loading | ok | fail
  const [message, setMessage] = useState("");
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    if (!token) {
      setState("fail");
      setMessage("Missing verification token.");
      return;
    }
    (async () => {
      try {
        await api.post("/auth/verify-email", { token });
        setState("ok");
        checkAuth();
      } catch (e) {
        setState("fail");
        setMessage(formatApiErrorDetail(e.response?.data?.detail) || "Verification failed.");
      }
    })();
  }, [token, checkAuth]);

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6">
      <div className="w-full max-w-md text-center">
        <Link to="/" className="inline-flex mb-8" data-testid="verify-brand">
          <Logo size={44} />
        </Link>
        {state === "loading" && (
          <div data-testid="verify-loading" className="mono-font text-sm text-white/60 tracking-widest uppercase pulse-glow">
            Verifying cryptographic signature…
          </div>
        )}
        {state === "ok" && (
          <div data-testid="verify-ok" className="border border-[#00FF66]/40 border-glow-green p-8">
            <CheckCircle size={40} weight="fill" className="text-[#00FF66] mx-auto" />
            <h1 className="heading-font text-2xl font-black tracking-tighter mt-4">Email verified.</h1>
            <p className="text-white/60 text-sm mt-2">Your account is fully activated.</p>
            <button onClick={() => navigate("/dashboard")} data-testid="verify-continue"
              className="mt-6 inline-flex items-center gap-3 px-5 py-3 bg-[#00FF66] text-black mono-font text-xs uppercase tracking-[0.25em] hover:bg-white transition-colors duration-200">
              Continue <ArrowRight weight="bold" size={14} />
            </button>
          </div>
        )}
        {state === "fail" && (
          <div data-testid="verify-fail" className="border border-[#FF0033]/40 border-glow-red p-8">
            <XCircle size={40} weight="fill" className="text-[#FF0033] mx-auto" />
            <h1 className="heading-font text-2xl font-black tracking-tighter mt-4">Verification failed</h1>
            <p className="text-white/60 text-sm mt-2">{message}</p>
            <Link to="/dashboard" data-testid="verify-fail-back"
              className="mt-6 inline-flex items-center gap-3 px-5 py-3 border border-white/20 mono-font text-xs uppercase tracking-[0.25em] hover:border-white/60 transition-colors duration-200">
              Return to Console
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
